import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { useShiftLookup } from '../../hooks/useShiftLookup';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { format } from 'date-fns';
import { AuthService } from '../../services/auth.service';
import { DashboardService } from '../../services/dashboard.service';
import { AttendanceStatus, AttendanceStatusUser } from '../../interfaces/dashboard.interface';
import { useToast } from '../../context/ToastContext';
import { displayUsername } from '../../utils/userUtils';
import { useTranslation } from 'react-i18next';

const VALID_STATUSES: AttendanceStatus[] = ['present', 'on-leave', 'absent'];

const STATUS_TITLE_KEYS = {
    'present': 'statusUserList.titles.present',
    'on-leave': 'statusUserList.titles.onLeave',
    'absent': 'statusUserList.titles.absent',
} as const;

const isValidStatus = (value: string | undefined): value is AttendanceStatus =>
    !!value && (VALID_STATUSES as string[]).includes(value);

const AttendanceStatusUserList: React.FC = () => {
    const { status } = useParams<{ status: string }>();
    const navigate = useNavigate();
    const toast = useToast();
    const { t } = useTranslation(['attendance', 'common']);

    const [users, setUsers] = useState<AttendanceStatusUser[]>([]);
    const [loading, setLoading] = useState(true);
    // NOTE: AttendanceStatusUser (from DashboardService) is a user-level summary and does not carry
    // per-attendance shift fields. The Shift filter dropdown is available for UI consistency but
    // cannot filter rows until the backend enriches this endpoint with shiftId per user.
    const { shifts } = useShiftLookup();

    const validStatus: AttendanceStatus | null = useMemo(
        () => (isValidStatus(status) ? status : null),
        [status]
    );

    useEffect(() => {
        if (!validStatus) {
            setLoading(false);
            return;
        }
        const fetchUsers = async () => {
            try {
                setLoading(true);
                const institutionId = AuthService.getSelectedInstitution();
                const result = await DashboardService.getAttendanceStatusUsers(institutionId, validStatus);
                setUsers(result);
            } catch (err) {
                toast.showApiError(err, t('statusUserList.loadFailed'));
            } finally {
                setLoading(false);
            }
        };
        fetchUsers();
    }, [validStatus, toast]);

    if (!validStatus) {
        return (
            <Box sx={{ padding: 3 }}>
                <Alert severity="error">
                    {t('statusUserList.invalidStatus', { status })}
                </Alert>
            </Box>
        );
    }

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ padding: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box>
                    <Typography variant="h5" fontWeight="bold">
                        {t(STATUS_TITLE_KEYS[validStatus])}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {format(new Date(), 'dd-MM-yyyy')} · {t('statusUserList.userCount', { count: users.length })}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    {/* Shift filter shown for UI consistency. Rows cannot be filtered by shift here
                        because AttendanceStatusUser does not carry shiftId; awaiting backend enrichment. */}
                    <FormControl sx={{ minWidth: 160 }} size="small">
                        <InputLabel id="shift-filter-label">{t('shiftFilter.label')}</InputLabel>
                        <Select
                            labelId="shift-filter-label"
                            value=""
                            label={t('shiftFilter.label')}
                            disabled
                        >
                            <MenuItem value="">{t('shiftFilter.all')}</MenuItem>
                            {shifts.filter(s => s.isActive).map(s => (
                                <MenuItem key={s.id} value={s.id}>{s.code} – {s.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/')}
                    >
                        {t('statusUserList.backToDashboard')}
                    </Button>
                </Box>
            </Box>

            {users.length === 0 ? (
                <Alert severity="info">{t('statusUserList.empty')}</Alert>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={tableHeaderStyle}>{t('common:fields.name')}</TableCell>
                                <TableCell sx={tableHeaderStyle}>{t('statusUserList.columns.email')}</TableCell>
                                <TableCell sx={tableHeaderStyle}>{t('common:fields.department')}</TableCell>
                                {/* Shift, Instance Status, Late, OT columns are not available on this page:
                                    AttendanceStatusUser is a user-level daily summary and does not carry
                                    per-attendance shift enrichment fields. They will be added once the
                                    DashboardService endpoint is enriched (future task). */}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.map((user) => (
                                <TableRow key={user.userId}>
                                    <TableCell sx={tableCellStyle}>{displayUsername(user.name) || '-'}</TableCell>
                                    <TableCell sx={tableCellStyle}>{user.email || '-'}</TableCell>
                                    <TableCell sx={tableCellStyle}>{user.department || '-'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

const tableHeaderStyle = {
    backgroundColor: '#f4f4f4',
    padding: '12px',
    textAlign: 'left' as const,
    borderBottom: '2px solid #ddd',
    fontWeight: 600,
};

const tableCellStyle = {
    padding: '12px',
    borderBottom: '1px solid #ddd',
};

export default AttendanceStatusUserList;
